import * as React from "react";
import { cn } from "../lib/utils";

/**
 * RuText — semantic wrapper for Russian prose.
 *
 * Sets `lang="ru"` so browser hyphenation engines apply Russian rules,
 * enables CSS `hyphens: auto`, and uses the project's Russian line-height
 * and letter-spacing tokens from globals.css.
 *
 * Use it for multi-sentence body text, chat messages, report paragraphs —
 * anywhere where browser typographic engines should know the language.
 *
 * Short labels (button text, badge labels) don't need wrapping.
 */
export interface RuTextProps extends React.HTMLAttributes<HTMLElement> {
  as?: "p" | "div" | "span" | "article" | "section" | "blockquote";
}

export function RuText({
  as: Tag = "p",
  className,
  ...props
}: RuTextProps) {
  return (
    <Tag
      lang="ru"
      className={cn("ru-text", className)}
      {...props}
    />
  );
}

/**
 * RuQuote — wraps children in Russian guillemets «…» as a presentational span.
 * For typographic purposes only — not a semantic block quote.
 */
export interface RuQuoteProps extends React.HTMLAttributes<HTMLSpanElement> {
  children: React.ReactNode;
}

export function RuQuote({ children, className, ...props }: RuQuoteProps) {
  return (
    <span
      lang="ru"
      className={cn("ru-quotes before:content-[open-quote] after:content-[close-quote]", className)}
      {...props}
    >
      {children}
    </span>
  );
}
